import { useQueryClient } from '@tanstack/react-query'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import axios from 'axios'
import { AssignTariffDataDto } from './dto/wallbox.dto'
import { assignTariffApiErrors } from './wallbox-api-errors'
import { ApiResult, usePortalMutation } from 'src/Libs/api'

interface UseAssignTariffHook {
  isLoading: boolean
  assignTariff: (payload: AssignTariffDataDto) => void
}

/**
 * Function for assign tariff to a wallbox data
 * @param payload - data off AssignTariffDataDto added to mutate react-query function
 * @param onClose - function for closing dialog on success method
 * @param installationId - unique installation identifier as a string
 * @returns isLoading as boolean
 * @returns mutate function from react-query
 */
export const useAssignTariffs = (onClose: () => void, installationId: number): UseAssignTariffHook => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResult()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const queryClient = useQueryClient()

  const assignTariffApi = async (payload: AssignTariffDataDto): Promise<ApiResult<string>> => {
    const assignTariffResponse = await authFetch<string>({
      path: 'tariffs/wallbox',
      endpoint: process.env.REACT_APP_HAGER_BRIDGE_SERVICE,
      method: 'PUT',
      payload: payload,
    })

    return new ApiResult(assignTariffResponse)
  }

  const { isPending, mutate: assignTariff } = usePortalMutation({
    mutationFn: assignTariffApi,
    onError: error => {
      if (axios.isAxiosError(error)) {
        enqueueErrorStack(t(assignTariffApiErrors[error.response?.data.errorCode]))
      } else {
        enqueueErrorStack(t('emobility.wallboxes.assign-tariff-failed'))
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallboxes', installationId] })
      enqueueSuccessStack(t('emobility.wallboxes.assign-tariff-success'))
      onClose()
    },
  })

  return { isLoading: isPending, assignTariff }
}
